import React, { useContext, useState, useRef, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import AuthContext from 'common/auth';

import Icons from 'assets/images/Icons';
import Logo from 'components/Logo';
import NotificationBell from 'components/NotificationBell';

import './MainMenu.scss';

// `onNavigate` lets the mobile drawer close itself when a destination is picked.
const MainMenu = ({ className, onNavigate }: { className: string, onNavigate?: () => void }) => {
  const { logout, user, selectedSubaccount, setSelectedSubaccount } = useContext(AuthContext)!;
  const queryClient = useQueryClient();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelectSubaccount = (sub: typeof selectedSubaccount) => {
    setSelectedSubaccount(sub);
    setDropdownOpen(false);
    queryClient.invalidateQueries();
  };

  const subaccounts = user?.subaccounts ?? [];
  const showPicker = subaccounts.length > 1;

  return (
    <header className={className}>
      <nav>
        <div className='brand-row'>
          <NavLink to='dashboard' className='brand' onClick={onNavigate} aria-label='ExaPool home'>
            <Logo />
          </NavLink>
          <NotificationBell onNavigate={onNavigate} />
        </div>
        {showPicker && (
          <div className='subaccount-picker' ref={dropdownRef}>
            <button
              className='subaccount-trigger'
              onClick={() => setDropdownOpen((o) => !o)}
              aria-haspopup='listbox'
              aria-expanded={dropdownOpen}
            >
              <Icons name='Account' />
              <span className='subaccount-name'>
                {selectedSubaccount ? selectedSubaccount.username : 'All accounts'}
              </span>
              <Icons name='ArrowUpDown' />
            </button>
            {dropdownOpen && (
              <ul className='subaccount-dropdown' role='listbox'>
                {subaccounts.map((sub) => (
                  <li
                    key={sub.id}
                    role='option'
                    aria-selected={selectedSubaccount?.id === sub.id}
                    onClick={() => handleSelectSubaccount(sub)}
                  >
                    {sub.username}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        <NavLink to='dashboard' className='mainLinks' onClick={onNavigate}>
          <span><Icons name='Dashboard' />Dashboard</span>
        </NavLink>
        <NavLink to='workers' className='mainLinks' onClick={onNavigate}>
          <span><Icons name='Workers' />Workers</span>
        </NavLink>
        <NavLink to='rewards' className='mainLinks' onClick={onNavigate}>
          <span><Icons name='Reward' />Rewards</span>
        </NavLink>
        <NavLink to='funds' className='mainLinks' onClick={onNavigate}>
          <span><Icons name='Wallet' />Funds</span>
        </NavLink>
        <NavLink to='ledger' className='mainLinks' onClick={onNavigate}>
          <span><Icons name='ArrowUpDown' />Ledger</span>
        </NavLink>
        <NavLink to='notifications' className='mainLinks' onClick={onNavigate}>
          <span><Icons name='Bell' />Notifications</span>
        </NavLink>
        <NavLink to='repair' className='mainLinks' onClick={onNavigate}>
          <span><Icons name='Settings' />Repair</span>
        </NavLink>
        <NavLink to='settings' className='mainLinks settings' onClick={onNavigate}>
          <span>
            <Icons name='Account' />
            Settings
            <Icons name='Setting' />
          </span>
        </NavLink>
        <button className='mainLinks username' onClick={logout}><Icons name='Logout' />Logout</button>
      </nav>
    </header>
  );
};

export default MainMenu;
